// El motor del recalculo de descuentos: consulta, calcula y escribe.
//
// La aritmetica vive aparte y pura en promocionRecalculoHelpers.js -- mismo
// reparto que pagosPlanHelpers con el recalculo de planes. Asi la vista previa y
// el efecto real no pueden discrepar: son la misma funcion.
import { supabase, applyOrgFilter, getActiveOrgOverride } from '../supabaseClient'
import { crearSaldoFavor } from './pagos'
import { calcularCambiosPorPromocion } from './promocionRecalculoHelpers'

// Lo llaman asignar, quitar y editar una promocion, y la herramienta de
// recalculo. Es idempotente: correrlo dos veces seguidas no cambia nada, asi que
// tambien sirve para reparar.
//
// Nunca escribe monto_pagado: lo cobrado solo lo mueve el registro de pagos.

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100

const CARGO_FIELDS = [
  'id', 'jugador_id', 'plan_id', 'concepto_id', 'temporada_id',
  'monto_original', 'descuento_porcentaje', 'descuento_monto', 'monto_final',
  'monto_pagado', 'promocion_id', 'estatus', 'temporadas:temporada_id(estado)',
].join(', ')

const PROMO_FIELDS = [
  'id', 'nombre', 'tipo', 'porcentaje', 'monto_fijo', 'config',
  'plan_id', 'concepto_id', 'temporada_id', 'activo', 'org_id',
].join(', ')

const resumenVacio = () => ({ jugadores: 0, cambios: [], creditos: [], historia: [], saldoGenerado: 0 })

// Los hijos de una familia ordenados por fecha de nacimiento, contando a TODOS
// (no solo a los que se estan recalculando). Posicion 1 = el mayor = sin
// descuento. Mismo criterio que asignarPlan.
const resolverPosicionFamiliar = async (jugadorIds) => {
  const jugadorFamilia = {}
  const jugadorPosicion = {}
  const { data: info } = await supabase
    .from('jugadores')
    .select('id, familia_id')
    .in('id', jugadorIds)
  ;(info || []).forEach((j) => { if (j.familia_id) jugadorFamilia[j.id] = j.familia_id })

  const familiaIds = [...new Set(Object.values(jugadorFamilia))]
  if (!familiaIds.length) return { jugadorFamilia, jugadorPosicion }

  const { data: hermanos } = await supabase
    .from('jugadores')
    .select('id, familia_id, fecha_nacimiento')
    .in('familia_id', familiaIds)
    .eq('activo', true)
    .or('deleted.is.null,deleted.eq.false')
    .order('fecha_nacimiento', { ascending: true })

  const porFamilia = {}
  ;(hermanos || []).forEach((j) => {
    if (!j.familia_id) return
    ;(porFamilia[j.familia_id] = porFamilia[j.familia_id] || []).push(j.id)
  })
  Object.values(porFamilia).forEach((miembros) => {
    miembros.forEach((id, idx) => { jugadorPosicion[id] = idx + 1 })
  })
  return { jugadorFamilia, jugadorPosicion }
}

// `extraPromociones` y `excluirPromocionId` existen para poder PREGUNTAR antes de
// escribir: "si le doy esta promocion, ¿que cargos bajan?" o "si se la quito,
// ¿que vuelve a costar?". Sin ellos la vista previa tendria que asignar primero y
// deshacer despues, que es justo lo que no queremos con dinero.
export const recalcularDescuentosDeJugadores = async (jugadorIds = [], {
  temporadaId = null, dryRun = false, extraPromociones = [], excluirPromocionId = null,
} = {}) => {
  try {
    const ids = [...new Set(jugadorIds)].filter(Boolean)
    if (!ids.length) return { data: resumenVacio(), error: null }

    // OJO: pagos_promociones_jugadores NO tiene org_id -- applyOrgFilter aqui
    // reventaria. El aislamiento viaja por la promocion, y el !inner convierte el
    // filtro sobre la tabla embebida en un join real.
    const orgId = getActiveOrgOverride()
    let promoQuery = supabase
      .from('pagos_promociones_jugadores')
      .select('jugador_id, pagos_promociones!inner(' + PROMO_FIELDS + ')')
      .in('jugador_id', ids)
      .eq('pagos_promociones.activo', true)
    if (orgId) promoQuery = promoQuery.eq('pagos_promociones.org_id', orgId)
    const { data: asignadas, error: promoErr } = await promoQuery
    if (promoErr) throw promoErr

    // Solo temporadas ABIERTAS: el trigger trg_bloquear_cerrada_cargos rechaza
    // cualquier escritura sobre las cerradas, y hace bien -- el pasado no se
    // reescribe. Se filtran aqui para no chocar contra el a media corrida.
    let cargoQuery = supabase
      .from('pagos_cargos')
      .select(CARGO_FIELDS)
      .in('jugador_id', ids)
      .neq('estatus', 'cancelado')
    if (temporadaId) cargoQuery = cargoQuery.eq('temporada_id', temporadaId)
    cargoQuery = applyOrgFilter(cargoQuery)
    const { data: cargosRaw, error: cargoErr } = await cargoQuery
    if (cargoErr) throw cargoErr
    const cargos = (cargosRaw || []).filter((c) => (c.temporadas?.estado || 'abierta') === 'abierta')
    if (!cargos.length) return { data: { ...resumenVacio(), jugadores: ids.length }, error: null }

    // Cuanto DINERO recibio cada cargo. Un pago con metodo 'saldo_favor' es
    // credito del club, no dinero de la familia: no congela el cargo.
    const cargoIds = cargos.map((c) => c.id)
    const pagadoRealPorCargo = {}
    const { data: detalles, error: detErr } = await supabase
      .from('pagos_transacciones_detalle')
      .select('cargo_id, monto_aplicado, pagos_transacciones!inner(metodo_pago, anulado)')
      .in('cargo_id', cargoIds)
    if (detErr) throw detErr
    ;(detalles || []).forEach((d) => {
      const tx = d.pagos_transacciones
      if (!tx || tx.anulado === true) return
      if (tx.metodo_pago === 'saldo_favor') return
      pagadoRealPorCargo[d.cargo_id] = round2((pagadoRealPorCargo[d.cargo_id] || 0) + Number(d.monto_aplicado || 0))
    })

    const { data: saldos, error: saldoErr } = await supabase
      .from('pagos_saldo_favor')
      .select('jugador_id, promocion_id, cargo_origen_id, monto')
      .in('jugador_id', ids)
      .eq('activo', true)
    if (saldoErr) throw saldoErr
    const creditosPorJugador = {}
    ;(saldos || []).forEach((s) => {
      if (!s.cargo_origen_id) return
      ;(creditosPorJugador[s.jugador_id] = creditosPorJugador[s.jugador_id] || []).push(s)
    })

    const { jugadorFamilia, jugadorPosicion } = await resolverPosicionFamiliar(ids)

    const resumen = { ...resumenVacio(), jugadores: ids.length }
    for (const jugadorId of ids) {
      const promosDelJugador = (asignadas || [])
        .filter((a) => a.jugador_id === jugadorId && a.pagos_promociones)
        .filter((a) => a.pagos_promociones.id !== excluirPromocionId)
        .map((a) => ({ ...a.pagos_promociones, _jugadorIds: [jugadorId] }))
        .concat((extraPromociones || []).map((p) => ({ ...p, _jugadorIds: [jugadorId] })))
      const susCargos = cargos.filter((c) => c.jugador_id === jugadorId)
      if (!susCargos.length) continue

      // Por temporada: una promocion con temporada solo alcanza a los cargos de
      // la suya; una sin temporada alcanza a todos. Antes esto no se miraba y un
      // descuento de este semestre rebajaba adeudos del anterior.
      const porTemporada = {}
      susCargos.forEach((c) => {
        const k = c.temporada_id || '_sin_temporada'
        ;(porTemporada[k] = porTemporada[k] || []).push(c)
      })

      for (const [temp, grupo] of Object.entries(porTemporada)) {
        const promos = promosDelJugador.filter((p) => !p.temporada_id || p.temporada_id === temp)
        const r = calcularCambiosPorPromocion({
          cargos: grupo, promociones: promos, jugadorId,
          jugadorFamilia, jugadorPosicion, pagadoRealPorCargo,
          creditosExistentes: creditosPorJugador[jugadorId] || [],
        })
        resumen.cambios.push(...r.cambios)
        resumen.creditos.push(...r.creditos)
        resumen.historia.push(...r.historia)
      }
    }
    resumen.saldoGenerado = round2(resumen.creditos.reduce((s, c) => s + Number(c.monto || 0), 0))

    if (dryRun) return { data: resumen, error: null }

    for (const ch of resumen.cambios) {
      const { error } = await supabase
        .from('pagos_cargos')
        .update({
          descuento_porcentaje: ch.descuento_porcentaje,
          descuento_monto: ch.descuento_monto,
          monto_final: ch.monto_final,
          saldo: ch.saldo,
          estatus: ch.estatus,
          promocion_id: ch.promocion_id,
        })
        .eq('id', ch.id)
      if (error) throw error
    }

    for (const cr of resumen.creditos) {
      const { error } = await crearSaldoFavor({
        jugadorId: cr.jugadorId,
        monto: cr.monto,
        origen: 'ajuste_promocion',
        descripcion: cr.motivo === 'historia'
          ? 'Descuento sobre un cargo que ya tenia pagos'
          : 'Ajuste por descuento',
        cargoOrigenId: cr.cargoId,
        temporadaId: cr.temporadaId,
        promocionId: cr.promocionId,
      })
      if (error) throw error
    }

    return { data: resumen, error: null }
  } catch (error) {
    console.error('[recalcularDescuentosDeJugadores]', error)
    return { data: null, error }
  }
}

export const recalcularDescuentosJugador = (jugadorId, opts = {}) =>
  recalcularDescuentosDeJugadores([jugadorId], opts)

// Pasar el motor por TODOS los que tienen alguna promocion viva. Alimenta la
// herramienta "Recalcular descuentos": como el motor es idempotente, correrla de
// mas no cuesta nada y sirve para reparar un desfase sin ir jugador por jugador.
//
// Sin temporada explicita alcanza a todas las abiertas, que es lo que el motor ya
// acota por su cuenta.
export const recalcularDescuentosDeLaOrg = async ({ temporadaId = null, dryRun = false } = {}) => {
  try {
    const orgId = getActiveOrgOverride()
    let q = supabase
      .from('pagos_promociones_jugadores')
      .select('jugador_id, pagos_promociones!inner(activo, org_id)')
      .eq('pagos_promociones.activo', true)
    if (orgId) q = q.eq('pagos_promociones.org_id', orgId)
    const { data, error } = await q
    if (error) throw error

    const ids = [...new Set((data || []).map((r) => r.jugador_id))]
    if (!ids.length) return { data: resumenVacio(), error: null }
    return recalcularDescuentosDeJugadores(ids, { temporadaId, dryRun })
  } catch (error) {
    console.error('[recalcularDescuentosDeLaOrg]', error)
    return { data: null, error }
  }
}
